import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // التوكن الآن في كوكي HttpOnly (لا يُقرأ من JS). نتذكّر المستخدم عبر بياناته غير الحسّاسة فقط.
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
      // تحقّق من الجلسة وتحديث البيانات — لو انتهت الجلسة يتكفّل interceptor بالـ401
      api.get('/profile')
        .then((res) => {
          const fresh = res.data?.data;
          if (fresh) {
            setUser(fresh);
            localStorage.setItem('user', JSON.stringify(fresh));
          }
        })
        .catch(() => { /* نبقي النسخة المخزّنة عند التعذّر المؤقت */ });
    }
    setIsLoading(false);
  }, []);

  // ملاحظة: التوكن يُحفظ الآن في كوكي HttpOnly من الخادم — لا نخزّنه في JavaScript.
  const login = (_token: string, newUser: User) => {
    setUser(newUser);
    localStorage.setItem('user', JSON.stringify(newUser));
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {}
    setUser(null);
    localStorage.removeItem('user');
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  return (
    <AuthContext.Provider value={{
      user,
      token: null,
      isAuthenticated: !!user,
      isLoading,
      login,
      logout,
      updateUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
